import { collection, getDocs, doc, updateDoc, serverTimestamp, setDoc, query, orderBy, limit, increment } from 'firebase/firestore';
import { db } from './firebase';
import { UserProfile } from '../context/AuthContext';

// Enhanced audit logger
export const logAdminAction = async (data: {
  adminUid: string;
  adminEmail: string;
  targetUid: string;
  targetEmail: string;
  action: string;
  prevValue?: any;
  newValue?: any;
  details?: any;
}) => {
  try {
    const logRef = doc(collection(db, 'admin_logs'));
    await setDoc(logRef, {
      ...data,
      timestamp: serverTimestamp()
    });
  } catch (error) {
    console.error("Failed to log admin action", error);
  }
};

export const logSecurityEvent = async (event: string, details: any) => {
  try {
    const logRef = doc(collection(db, 'security_logs'));
    await setDoc(logRef, {
      event,
      details,
      timestamp: serverTimestamp()
    });
  } catch (error) {
    console.error("Failed to log security event", error);
  }
};

export const getAllUsers = async (): Promise<UserProfile[]> => {
  try {
    const snapshot = await getDocs(collection(db, 'users'));
    return snapshot.docs.map(doc => ({ uid: doc.id, ...doc.data() } as UserProfile));
  } catch (error) {
    console.error("Failed to fetch users", error);
    return [];
  }
};

export const updateUserRole = async (admin: { uid: string, email: string }, target: { uid: string, email: string }, newRole: 'user' | 'admin' | 'super_admin', prevRole: string) => {
  try {
    await updateDoc(doc(db, 'users', target.uid), { role: newRole });
    await logAdminAction({
      adminUid: admin.uid,
      adminEmail: admin.email,
      targetUid: target.uid,
      targetEmail: target.email,
      action: 'UPDATE_ROLE',
      prevValue: prevRole,
      newValue: newRole
    });
  } catch (error) {
    console.error("Failed to update role", error);
    throw error;
  }
};

export const updateUserStatus = async (admin: { uid: string, email: string }, target: { uid: string, email: string }, newStatus: 'active' | 'suspended' | 'deleted', prevStatus: string) => {
  try {
    await updateDoc(doc(db, 'users', target.uid), { status: newStatus });
    await logAdminAction({
      adminUid: admin.uid,
      adminEmail: admin.email,
      targetUid: target.uid,
      targetEmail: target.email,
      action: 'UPDATE_STATUS',
      prevValue: prevStatus,
      newValue: newStatus
    });
  } catch (error) {
    console.error("Failed to update status", error);
    throw error;
  }
};

export const grantPro = async (admin: { uid: string, email: string }, target: { uid: string, email: string }, expiryDate: Date | null, reason: string) => {
  try {
    const updateData: any = {
      isPro: true,
      plan: 'pro',
      proStatus: 'active',
      proGrantedBy: admin.email,
      proGrantedAt: serverTimestamp(),
      proExpiresAt: expiryDate,
      proReason: reason,
      creditsMode: 'unlimited',
      updatedAt: serverTimestamp()
    };

    await updateDoc(doc(db, 'users', target.uid), updateData);
    
    await logAdminAction({
      adminUid: admin.uid,
      adminEmail: admin.email,
      targetUid: target.uid,
      targetEmail: target.email,
      action: 'GRANT_PRO',
      prevValue: 'free',
      newValue: 'pro',
      details: { 
        duration: expiryDate ? `Expires: ${expiryDate.toISOString()}` : 'Lifetime',
        reason 
      }
    });
  } catch (error) {
    console.error("Failed to grant pro", error);
    throw error;
  }
};

export const removePro = async (admin: { uid: string, email: string }, target: { uid: string, email: string }, reason: string) => {
  try {
    await updateDoc(doc(db, 'users', target.uid), { 
      isPro: false,
      plan: 'free',
      proStatus: 'inactive',
      creditsMode: 'credits',
      proRemovedBy: admin.email,
      proRemovedAt: serverTimestamp(),
      proRemoveReason: reason,
      updatedAt: serverTimestamp()
    });
    await logAdminAction({
      adminUid: admin.uid,
      adminEmail: admin.email,
      targetUid: target.uid,
      targetEmail: target.email,
      action: 'REMOVE_PRO',
      prevValue: 'pro',
      newValue: 'free',
      details: { reason }
    });
  } catch (error) {
    console.error("Failed to remove pro", error);
    throw error;
  }
};

export const addAdminUser = async (adminId: string, email: string) => {
  try {
    await setDoc(doc(db, 'admin_users', email), {
      email,
      role: 'admin',
      addedBy: adminId,
      createdAt: serverTimestamp(),
      status: 'active'
    });
    // For addAdminUser, we don't have all details easily but it's okay for now or we could expand it later
    // await logAdminAction(...) 
  } catch (error) {
    console.error("Failed to add admin", error);
    throw error;
  }
};

export const getAdminReports = async () => {
  try {
    const snapshot = await getDocs(collection(db, 'reports'));
    return snapshot.docs.map(doc => ({ id: doc.id, ...(doc.data() as object) }));
  } catch (error) {
    console.error("Failed to fetch reports", error);
    return [];
  }
};

export const getAdminLogs = async (limitCount: number = 50) => {
  try {
    const q = query(collection(db, 'admin_logs'), orderBy('timestamp', 'desc'), limit(limitCount));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...(doc.data() as object) }));
  } catch (error) {
    console.error("Failed to fetch admin logs", error);
    return [];
  }
};

export const getSecurityLogs = async (limitCount: number = 50) => {
  try {
    const q = query(collection(db, 'security_logs'), orderBy('timestamp', 'desc'), limit(limitCount));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...(doc.data() as object) }));
  } catch (error) {
    console.error("Failed to fetch security logs", error);
    return [];
  }
};

export const getAllAdmins = async () => {
  try {
    const snapshot = await getDocs(collection(db, 'admin_users'));
    return snapshot.docs.map(doc => ({ id: doc.id, ...(doc.data() as object) }));
  } catch (error) {
    console.error("Failed to fetch admins", error);
    return [];
  }
};

export const removeAdminUser = async (adminId: string, email: string) => {
  try {
    await updateDoc(doc(db, 'admin_users', email), { status: 'revoked' });
  } catch (error) {
    console.error("Failed to revoke admin", error);
    throw error;
  }
};

export const adjustCredits = async (
  idToken: string,
  targetUid: string,
  delta: number,
  reason: string
) => {
  const response = await fetch('/api/admin/adjust-credits', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`
    },
    body: JSON.stringify({ targetUid, delta, reason })
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Failed to adjust credits');
  }
  return data;
};

export const setCredits = async (
  idToken: string,
  targetUid: string,
  newCredits: number,
  prevCredits: number,
  reason: string
) => {
  const delta = newCredits - prevCredits;
  return adjustCredits(idToken, targetUid, delta, reason);
};

export const repairUserCredits = async (admin: { uid: string, email: string }, target: UserProfile) => {
  try {
    const currentAvailable = target.credits || 0;
    const used = target.reports_used || 0;
    
    // Normalization logic: Available credits must never be negative.
    // If they are negative, it means the total granted was less than the usage.
    // We fix this by setting available to 0. This implicitly increases "Total Granted" (Available + Used) to match Used.
    if (currentAvailable < 0) {
      await updateDoc(doc(db, 'users', target.uid), {
        credits: 0,
        updatedAt: serverTimestamp()
      });
      await logAdminAction({
        adminUid: admin.uid,
        adminEmail: admin.email,
        targetUid: target.uid,
        targetEmail: target.email,
        action: 'REPAIR_CREDITS',
        prevValue: currentAvailable,
        newValue: 0,
        details: { reason: 'Normalized negative credits to zero' }
      });
      return true;
    }
    return false;
  } catch (error) {
    console.error("Failed to repair credits", error);
    throw error;
  }
};

export const getAllPayments = async () => {
  try {
    const snapshot = await getDocs(query(collection(db, 'payments'), orderBy('createdAt', 'desc')));
    return snapshot.docs.map(doc => ({ id: doc.id, ...(doc.data() as object) }));
  } catch (error) {
    console.error("Failed to fetch payments", error);
    return [];
  }
};

export const getFinanceStats = async () => {
  try {
    const payments = await getAllPayments() as any[];
    const totalRevenue = payments.reduce((acc, p) => acc + (p.amount || 0), 0);
    const singleCount = payments.filter(p => p.plan === 'single').length;
    const familyCount = payments.filter(p => p.plan === 'family').length;
    const totalCreditsSold = payments.reduce((acc, p) => acc + (p.creditsAdded || 0), 0);

    return {
      totalRevenue,
      paidUsers: new Set(payments.map(p => p.uid)).size,
      singleCount,
      familyCount,
      totalCreditsSold,
      refundsPending: 0, // Placeholder
      providerStatus: 'Connected (Stripe Live)'
    };
  } catch (error) {
    console.error("Failed to calculate finance stats", error);
    return null;
  }
};

